import AuthLayout from "layouts/Auth";
import AdminLayout from "layouts/admin/Admin";
import React from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import * as Sentry from "@sentry/react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { isAxiosError } from "axios";
import { createRoot } from "react-dom/client";
import { useProfile } from "./hooks/useProfile";
import { AuthProvider } from "./reducers/auth";
import { RefererProvider, useSetReferer } from "./reducers/referer";
import { ViewerModeProvider } from "./reducers/viewerMode";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      console.error(`[QueryError] ${JSON.stringify(query.queryKey)}:`, error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      console.error("[MutationError]:", error);
    },
  }),
  defaultOptions: {
    queries: {
      refetchInterval: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: (failureCount, error) => {
        if (
          isAxiosError(error) &&
          error.response?.status &&
          error.response.status < 500
        ) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

const App = (): React.ReactElement => {
  const setReferer = useSetReferer();
  const location = useLocation();

  React.useEffect(() => {
    setReferer(location);
  }, [location, setReferer]);

  const userProfile = useProfile();
  return (
    <SentryRoutes>
      {userProfile && <Route path="/admin/*" element={<AdminLayout />} />}
      <Route path="/auth/*" element={<AuthLayout />} />
      <Route path={"*"} element={<Navigate replace to={"/auth/login"} />} />
    </SentryRoutes>
  );
};

export const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const RootApp = (element?: HTMLElement): void => {
  const root = createRoot(
    element || (document.getElementById("root") as HTMLElement),
  );
  root.render(
    <Sentry.ErrorBoundary
      fallback={
        window.runConfig?.environment === "production" ? (
          <p>An error has occurred</p>
        ) : (
          ({ error, componentStack, resetError }) => (
            <React.Fragment>
              <div>You have encountered an error</div>
              <div>{JSON.stringify(error)}</div>
              <div>{componentStack}</div>
              <button
                type={"button"}
                onClick={() => {
                  resetError();
                }}
              >
                Click here to reset!
              </button>
            </React.Fragment>
          )
        )
      }
    >
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <BrowserRouter>
          <RefererProvider>
            <AuthProvider>
              <ViewerModeProvider>
                <App />
              </ViewerModeProvider>
            </AuthProvider>
          </RefererProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>,
  );
};

export default RootApp;
